import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SvgIcon from '@mui/material/SvgIcon';

import BarChartSquare02Icon from 'src/icons/untitled-ui/duocolor/bar-chart-square-02';
import Building04Icon from 'src/icons/untitled-ui/duocolor/building-04';
import LineChartUp04Icon from 'src/icons/untitled-ui/duocolor/line-chart-up-04';
import Users03Icon from 'src/icons/untitled-ui/duocolor/users-03';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CampaignIcon from '@mui/icons-material/Campaign';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentsIcon from '@mui/icons-material/Payments';
import LockIcon from '@mui/icons-material/Lock';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { tokens } from 'src/locales/tokens';
import { paths } from 'src/paths';
import { useAuth } from 'src/hooks/use-auth';
import { canAccess, getSideNavPermissions } from 'src/utils/permission-check';

const SUB_ICONS = {
  tablero: DashboardIcon,
  estructuracion: FactCheckIcon,
  llamadoCapital: CampaignIcon,
  cronograma: CalendarMonthIcon,
  seguimiento: TimelineIcon,
  inversionistas: PeopleIcon,
  pymes: BusinessIcon,
  archivos: FolderIcon,
  alimentar: UploadFileIcon,
  dashboard: DashboardIcon,
  oportunidades: TrendingUpIcon,
  misInversiones: AccountBalanceWalletIcon,
  historial: HistoryIcon,
  solicitar: NoteAddIcon,
  cotizador: PercentIcon,
  honorarios: PaymentsIcon,
  cierre: LockIcon,
  facturacion: ReceiptLongIcon,
  contrato: DescriptionIcon,
  alertas: NotificationsIcon,
  reportes: AssessmentIcon,
};

const subIcono = (key) => {
  const Icon = SUB_ICONS[key];
  return Icon ? (
    <SvgIcon fontSize="small">
      <Icon />
    </SvgIcon>
  ) : null;
};

const ROLE_GROUP = {
  garante: 'operaciones',
  inversionista: 'inversionista',
  pyme: 'pyme',
};

const EXTRA_KEYS = [
  'dashboard:global',
  'users:list',
  'users:manage',
  'roles:assign',
  'permissions:assign',
  'seace:view',
  'mivivienda:view',
];

export const useSections = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return useMemo(() => {
    const items = [];
    const permissions = user?.permissions || [];
    const navGroups = getSideNavPermissions(permissions);
    const openByDefault =
      user?.is_super || (user?.role === 'garante' && user?.sub_role === 'administrador')
        ? false
        : true;

    const esAdmin =
      user?.is_super || (user?.role === 'garante' && user?.sub_role === 'administrador');

    const gruposConPermiso = ['operaciones', 'inversionista', 'pyme'].filter(
      (g) => (navGroups[g] || []).length > 0
    );
    const tieneExtra = EXTRA_KEYS.some((k) => permissions.includes(k));
    const soloRol = !esAdmin && gruposConPermiso.length === 1 && !tieneExtra;
    const aplanar = (grupo) => soloRol && gruposConPermiso[0] === grupo;

    if (canAccess(user, 'dashboard:global') || user?.is_super) {
      items.push({
        title: 'Dashboard Global',
        path: paths.dashboard.admin,
        icon: (
          <SvgIcon fontSize="small">
            <LineChartUp04Icon />
          </SvgIcon>
        ),
      });
    }

    if (canAccess(user, 'permissions:assign') || user?.is_super) {
      items.push({
        title: 'Roles y Privilegios',
        path: '/dashboard/admin/permisos',
        icon: (
          <SvgIcon fontSize="small">
            <Users03Icon />
          </SvgIcon>
        ),
      });
    }

    if (canAccess(user, 'users:list')) {
      items.push({
        title: t(tokens.nav.users),
        path: paths.dashboard.users,
        icon: (
          <SvgIcon fontSize="small">
            <Users03Icon />
          </SvgIcon>
        ),
      });
    }

    if (navGroups.operaciones) {
      const opItems = [];
      if (navGroups.operaciones.includes('tablero'))
        opItems.push({
          title: t(tokens.nav.panel),
          path: paths.dashboard.operaciones.tablero,
          icon: subIcono('tablero'),
        });
      if (navGroups.operaciones.includes('estructuracion'))
        opItems.push({
          title: t(tokens.nav.estructuracion),
          path: paths.dashboard.operaciones.estructuracion,
          icon: subIcono('estructuracion'),
        });
      if (navGroups.operaciones.includes('llamadoCapital'))
        opItems.push({
          title: t(tokens.nav.llamadoCapital),
          path: paths.dashboard.operaciones.llamadoCapital,
          icon: subIcono('llamadoCapital'),
        });
      if (navGroups.operaciones.includes('cronograma'))
        opItems.push({
          title: t(tokens.nav.cronograma),
          path: paths.dashboard.operaciones.cronograma,
          icon: subIcono('cronograma'),
        });
      if (navGroups.operaciones.includes('seguimiento'))
        opItems.push({
          title: t(tokens.nav.seguimiento),
          path: paths.dashboard.operaciones.seguimiento,
          icon: subIcono('seguimiento'),
        });
      if (navGroups.operaciones.includes('inversionistas'))
        opItems.push({
          title: t(tokens.nav.inversionistas),
          path: paths.dashboard.operaciones.inversionistas,
          icon: subIcono('inversionistas'),
        });
      if (navGroups.operaciones.includes('pymes'))
        opItems.push({
          title: t(tokens.nav.pymes),
          path: paths.dashboard.operaciones.pymes,
          icon: subIcono('pymes'),
        });
      if (navGroups.operaciones.includes('archivos'))
        opItems.push({
          title: t(tokens.nav.archivos),
          path: paths.dashboard.operaciones.archivos,
          icon: subIcono('archivos'),
        });
      if (navGroups.operaciones.includes('alimentar'))
        opItems.push({
          title: 'Alimentar al Sistema',
          path: paths.dashboard.operaciones.alimentar,
          icon: subIcono('alimentar'),
        });
      opItems.push({
        title: 'Reportes',
        path: '/dashboard/operaciones/reportes',
        icon: subIcono('reportes'),
      });

      if (aplanar('operaciones')) {
        items.push(...opItems);
      } else if (opItems.length > 0) {
        items.push({
          title: t(tokens.nav.operaciones),
          icon: (
            <SvgIcon fontSize="small">
              <LineChartUp04Icon />
            </SvgIcon>
          ),
          items: opItems,
          open: openByDefault,
        });
      }
    }

    if (navGroups.inversionista) {
      const invItems = [];
      if (navGroups.inversionista.includes('dashboard'))
        invItems.push({
          title: t(tokens.nav.panel),
          path: paths.dashboard.inversionista.dashboard,
          icon: subIcono('dashboard'),
        });
      if (navGroups.inversionista.includes('oportunidades'))
        invItems.push({
          title: t(tokens.nav.oportunidades),
          path: paths.dashboard.inversionista.oportunidades,
          icon: subIcono('oportunidades'),
        });
      if (navGroups.inversionista.includes('misInversiones'))
        invItems.push({
          title: t(tokens.nav.inversionesActivas),
          path: paths.dashboard.inversionista.misInversiones,
          icon: subIcono('misInversiones'),
        });
      if (navGroups.inversionista.includes('historial'))
        invItems.push({
          title: t(tokens.nav.historial),
          path: paths.dashboard.inversionista.historial,
          icon: subIcono('historial'),
        });
      if (navGroups.inversionista.includes('archivos'))
        invItems.push({
          title: t(tokens.nav.archivos),
          path: paths.dashboard.inversionista.archivos,
          icon: subIcono('archivos'),
        });
      invItems.push({
        title: 'Reportes',
        path: '/dashboard/inversionista/reportes',
        icon: subIcono('reportes'),
      });

      if (aplanar('inversionista')) {
        items.push(...invItems);
      } else if (invItems.length > 0) {
        items.push({
          title: t(tokens.nav.misInversiones),
          icon: (
            <SvgIcon fontSize="small">
              <LineChartUp04Icon />
            </SvgIcon>
          ),
          items: invItems,
          open: openByDefault,
        });
      }
    }

    if (navGroups.pyme) {
      const pymeItems = [];
      if (navGroups.pyme.includes('dashboard'))
        pymeItems.push({
          title: t(tokens.nav.panel),
          path: paths.dashboard.pyme.dashboard,
          icon: subIcono('dashboard'),
        });
      if (navGroups.pyme.includes('solicitar'))
        pymeItems.push({
          title: t(tokens.nav.solicitar),
          path: paths.dashboard.pyme.solicitar,
          icon: subIcono('solicitar'),
        });
      if (navGroups.pyme.includes('cotizador'))
        pymeItems.push({
          title: t(tokens.nav.cotizador),
          path: paths.dashboard.pyme.cotizador,
          icon: subIcono('cotizador'),
        });
      if (navGroups.pyme.includes('cronograma'))
        pymeItems.push({
          title: t(tokens.nav.cronograma),
          path: paths.dashboard.pyme.cronograma,
          icon: subIcono('cronograma'),
        });
      if (navGroups.pyme.includes('honorarios'))
        pymeItems.push({
          title: t(tokens.nav.honorarios),
          path: paths.dashboard.pyme.honorarios,
          icon: subIcono('honorarios'),
        });
      if (navGroups.pyme.includes('cierre'))
        pymeItems.push({
          title: t(tokens.nav.cierre),
          path: paths.dashboard.pyme.cierre,
          icon: subIcono('cierre'),
        });
      if (navGroups.pyme.includes('archivos'))
        pymeItems.push({
          title: t(tokens.nav.archivos),
          path: paths.dashboard.pyme.archivos,
          icon: subIcono('archivos'),
        });
      if (navGroups.pyme.includes('facturacion'))
        pymeItems.push({
          title: t(tokens.nav.facturacion),
          path: paths.dashboard.pyme.facturacion,
          icon: subIcono('facturacion'),
        });
      if (navGroups.pyme.includes('contrato'))
        pymeItems.push({
          title: t(tokens.nav.contrato),
          path: paths.dashboard.pyme.contrato,
          icon: subIcono('contrato'),
        });
      if (navGroups.pyme.includes('alertas'))
        pymeItems.push({
          title: t(tokens.nav.alertas),
          path: paths.dashboard.pyme.alertas,
          icon: subIcono('alertas'),
        });
      pymeItems.push({
        title: 'Reportes',
        path: '/dashboard/pyme/reportes',
        icon: subIcono('reportes'),
      });

      if (aplanar('pyme')) {
        items.push(...pymeItems);
      } else if (pymeItems.length > 0) {
        items.push({
          title: t(tokens.nav.miPyme),
          icon: (
            <SvgIcon fontSize="small">
              <LineChartUp04Icon />
            </SvgIcon>
          ),
          items: pymeItems,
          open: openByDefault,
        });
      }
    }

    if (canAccess(user, 'seace:view')) {
      items.push({
        title: t(tokens.nav.seace),
        path: paths.dashboard.analytics.seace,
        icon: (
          <SvgIcon fontSize="small">
            <Building04Icon />
          </SvgIcon>
        ),
      });
    }

    if (canAccess(user, 'mivivienda:view')) {
      items.push({
        title: t(tokens.nav.mivivienda),
        path: paths.dashboard.analytics.mivivienda,
        icon: (
          <SvgIcon fontSize="small">
            <BarChartSquare02Icon />
          </SvgIcon>
        ),
      });
    }

    return [{ items }];
  }, [t, user]);
};
